import { cfetch } from '@confluence/network';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

/**
 * Local type for media token config (matches OpenAPI SnippetMediaTokenConfig).
 */
type SnippetMediaTokenConfig = {
	clientId: string;
	fileStoreUrl: string;
	maxFileSize?: number | null;
};

/**
 * Local type for media token API response (matches OpenAPI SnippetMediaTokenResponse).
 */
type SnippetMediaTokenResponse = {
	token: string;
	expirationTime: number;
	config: SnippetMediaTokenConfig;
	collectionNames: Record<string, string>;
};

export type SnippetMediaAuth = {
	clientId: string;
	token: string;
	baseUrl: string;
	collectionId?: string;
};

const BLOCK_SERVICE_SNIPPETS_MEDIA_TOKEN_URL = '/gateway/api/blocks/v1/snippets';

/**
 * Fetches a media token for a snippet from the Block Service (GET /v1/snippets/{snippetId}/media-token).
 * Returns auth shape compatible with MediaProvider's AuthProvider.
 * collectionId is resolved from the response collectionNames map when present for the snippetId.
 */
export const fetchSnippetMediaToken = async (snippetId: string): Promise<SnippetMediaAuth> => {
	try {
		const response = await cfetch(
			`${BLOCK_SERVICE_SNIPPETS_MEDIA_TOKEN_URL}/${snippetId}/media-token`,
			{
				method: 'GET',
				headers: {
					Accept: 'application/json',
				},
			},
		);

		const data: SnippetMediaTokenResponse = await response.json();
		const collectionId = data.collectionNames?.[snippetId];

		return {
			clientId: data.config.clientId,
			token: data.token,
			baseUrl: data.config.fileStoreUrl,
			...(collectionId !== undefined && { collectionId }),
		};
	} catch (error) {
		getMonitoringClient().submitError(error, {
			attribution: Attribution.INTEGRATIONS,
		});
		throw error;
	}
};
